import React, { useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import { SecondaryButton } from '@components/Button/variants';
import Spinner from '@components/Spinner';
import useEventTracking from '@hooks/useEventTracking';
import useSubscriberComments from '@hooks/useSubscriberComments';
import { forum_path, subscriber_profile_path } from '@routes';
import { ClickTarget, TrackingLocation, click, impression } from '@tracking';
import type { SubscriberComment } from '@utils/backend-api/subscriber_directory';

import PersonalActivityCard from './PersonalActivityCard';
import ForYouSection from './Section';
import { FOR_YOU_ENGAGEMENT_LABELS, FOR_YOU_SECTION_IDS, VIEW_ALL } from './constants';
import type { ForYouCommunityInsightsProps } from './interfaces';
import useRegisterSection from './useRegisterSection';

const COMMENT_TYPE = 'comment';
const MAX_RECENT_ACTIVITY_ITEMS = 4;

const ForYouCommunityInsights = ({ initialCurrentUser }: ForYouCommunityInsightsProps) => {
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.ForumUnit,
  });

  const { data: recentActivityData, isLoading: isLoadingRecentActivity } = useSubscriberComments({
    commentType: 'recent_activity',
    userId: initialCurrentUser?.id,
    username: initialCurrentUser?.username,
    enabled: !!initialCurrentUser,
    limit: MAX_RECENT_ACTIVITY_ITEMS,
  });

  const recentActivity = recentActivityData?.pages?.[0]?.comments;
  const hasPersonalActivity = !!recentActivity && recentActivity.length > 0;

  const communityInsightsDisplayText = useMemo(() => {
    if (!initialCurrentUser) return FOR_YOU_ENGAGEMENT_LABELS.NotEngaged.CommunityInsights;

    return initialCurrentUser.has_commented_in_forum
      ? FOR_YOU_ENGAGEMENT_LABELS.Engaged.CommunityInsights
      : FOR_YOU_ENGAGEMENT_LABELS.NotEngaged.CommunityInsights;
  }, [initialCurrentUser]);

  useRegisterSection(FOR_YOU_SECTION_IDS.CommunityInsights, hasPersonalActivity);

  if (!hasPersonalActivity) return null;

  const viewAllUrl = hasPersonalActivity
    ? subscriber_profile_path({ username: initialCurrentUser!.username, post_type: 'comments' })
    : forum_path();

  const onItemClick = (text: string, type: string) => {
    trackEvent(
      click({
        clickTarget: type === COMMENT_TYPE ? ClickTarget.CommentPost : ClickTarget.ForumPost,
        clickText: String(text),
      })
    );
  };

  const onViewAllClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.ViewMore, clickText: VIEW_ALL }));
  };

  return (
    <Track>
      <ForYouSection
        id={FOR_YOU_SECTION_IDS.CommunityInsights}
        highlight={communityInsightsDisplayText.label}
        subtitle={communityInsightsDisplayText.subtitle}
        title={communityInsightsDisplayText.title}
      >
        {isLoadingRecentActivity ? (
          <Spinner marginY="small" />
        ) : (
          <InView
            onChange={(inView) => {
              inView &&
                trackEvent(
                  impression({ impressionElement: FOR_YOU_SECTION_IDS.CommunityInsights })
                );
            }}
            triggerOnce
          >
            <div className="relative mt-16 md:mt-48">
              <div className="grid grid-cols-1 gap-16 sm:grid-cols-2 sm:gap-32 md:grid-cols-4 md:gap-24">
                {hasPersonalActivity &&
                  recentActivity.map((item: SubscriberComment, index: number) => (
                    <PersonalActivityCard
                      key={`${item.type}-${index}`}
                      item={item}
                      onClick={onItemClick}
                    />
                  ))}
              </div>
              {viewAllUrl && (
                <SecondaryButton
                  as="link"
                  className="mt-32 md:mt-48"
                  href={viewAllUrl}
                  onClickCapture={onViewAllClick}
                >
                  {VIEW_ALL}
                </SecondaryButton>
              )}
            </div>
          </InView>
        )}
      </ForYouSection>
    </Track>
  );
};

export default ForYouCommunityInsights;
